import React, { useState, useEffect, useRef } from 'react';
import { useAnalyticsTopics } from '../hooks/useAnalyticsTopics';

/**
 * AnalyticsFieldset — Renders dynamic topic buttons in the Analytics fieldset.
 *
 * Replaces the inline GROUP 2 Analytics JSX from ClientApplication.js with a
 * standalone component driven by the backend Topic Resolver API.
 *
 * Preserves existing UI: pill buttons (blue palette), config popover (⚙️),
 * menu dropdown (▼ Menu), and localStorage pin overrides.
 *
 * Props:
 * - clientId: string — Subscriber identifier
 * - productType: string — Product type string (e.g., "Restaurant.Type-1")
 * - onQuery: (query: string) => void — Calls handleGenaiQuery
 * - customAnalyticsItems: Array — User-added custom analytics items
 * - setCustomAnalyticsItems: Function — Setter for custom analytics items
 */
function AnalyticsFieldset({
  clientId,
  productType,
  onQuery,
  customAnalyticsItems = [],
  setCustomAnalyticsItems,
}) {
  // Fetch dynamic topics from backend Topic Resolver API
  const { topics, loading, error } = useAnalyticsTopics(clientId, productType);

  // Track topics that have failed SQL execution at runtime
  // Key: category or label string, Value: error message
  const [failedTopics, setFailedTopics] = useState({});

  // Detect topics arriving from API with validation_status: "error"
  useEffect(() => {
    if (topics && topics.length > 0) {
      const apiFailedTopics = {};
      topics.forEach(t => {
        const key = t.category || t.label;
        if (t.validation_status === 'error' || t.validation_status === 'validation_failed') {
          apiFailedTopics[key] = 'SQL validation failed on the server';
        }
      });
      if (Object.keys(apiFailedTopics).length > 0) {
        setFailedTopics(prev => ({ ...prev, ...apiFailedTopics }));
      }
    }
  }, [topics]);

  /**
   * Wrapper around onQuery that catches runtime SQL execution errors.
   * Marks the topic as failed if the query throws or returns a rejected promise.
   * Does NOT remove the button — keeps it visible with an error indicator.
   */
  const handleTopicClick = async (query, topicKey) => {
    try {
      const result = onQuery(query);
      if (result && typeof result.then === 'function') {
        await result;
      }
      if (topicKey && failedTopics[topicKey]) {
        setFailedTopics(prev => {
          const updated = { ...prev };
          delete updated[topicKey];
          return updated;
        });
      }
    } catch (err) {
      if (topicKey) {
        setFailedTopics(prev => ({
          ...prev,
          [topicKey]: err.message || 'Query execution failed',
        }));
      }
    }
  };

  // Configurable button count (2 or 3) — persisted per client in localStorage
  const [analyticsBtnCount, setAnalyticsBtnCount] = useState(() => {
    const saved = localStorage.getItem(`pnp_analytics_btncount_${clientId}`);
    return saved ? parseInt(saved, 10) : 3;
  });

  // Pinned button overrides from localStorage
  const [pinnedBtn1Label, setPinnedBtn1Label] = useState(() => localStorage.getItem(`pnp_analytics_lbl_${clientId}`) || '');
  const [pinnedBtn1Query, setPinnedBtn1Query] = useState(() => localStorage.getItem(`pnp_analytics_q_${clientId}`) || '');
  const [pinnedBtn2Label, setPinnedBtn2Label] = useState(() => localStorage.getItem(`pnp_analytics_lbl2_${clientId}`) || '');
  const [pinnedBtn2Query, setPinnedBtn2Query] = useState(() => localStorage.getItem(`pnp_analytics_q2_${clientId}`) || '');
  const [pinnedBtn3Label, setPinnedBtn3Label] = useState(() => localStorage.getItem(`pnp_analytics_lbl3_${clientId}`) || '');
  const [pinnedBtn3Query, setPinnedBtn3Query] = useState(() => localStorage.getItem(`pnp_analytics_q3_${clientId}`) || '');

  // UI state for menus/popovers
  const [showAnalyticsMenu, setShowAnalyticsMenu] = useState(false);
  const [showAnalyticsConfig, setShowAnalyticsConfig] = useState(false);

  // Expanded multi-question topic dropdown: null = closed, object = { label, questions }
  const [expandedTopic, setExpandedTopic] = useState(null);

  // Ref for outside-click detection on expanded topic dropdown
  const expandedDropdownRef = useRef(null);

  // Re-read localStorage when clientId changes
  useEffect(() => {
    const saved = localStorage.getItem(`pnp_analytics_btncount_${clientId}`);
    setAnalyticsBtnCount(saved ? parseInt(saved, 10) : 3);
    setPinnedBtn1Label(localStorage.getItem(`pnp_analytics_lbl_${clientId}`) || '');
    setPinnedBtn1Query(localStorage.getItem(`pnp_analytics_q_${clientId}`) || '');
    setPinnedBtn2Label(localStorage.getItem(`pnp_analytics_lbl2_${clientId}`) || '');
    setPinnedBtn2Query(localStorage.getItem(`pnp_analytics_q2_${clientId}`) || '');
    setPinnedBtn3Label(localStorage.getItem(`pnp_analytics_lbl3_${clientId}`) || '');
    setPinnedBtn3Query(localStorage.getItem(`pnp_analytics_q3_${clientId}`) || '');
  }, [clientId]);

  // Close expanded topic dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expandedDropdownRef.current && !expandedDropdownRef.current.contains(event.target)) {
        setExpandedTopic(null);
      }
    };
    if (expandedTopic) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedTopic]);

  // Build the effective button list from topics + custom items (deduplicated)
  const buildAllAnalytics = () => {
    const topicItems = topics.map(t => ({
      id: t.category || t.label,
      icon: t.icon || '📊',
      label: t.label,
      query: t.sample_query,
      questions: t.questions || [],
      question_count: t.question_count || 1,
    }));
    const seen = new Set();
    return [...topicItems, ...customAnalyticsItems].filter(op => {
      const key = op.label.toLowerCase().trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const allAnalytics = buildAllAnalytics();

  // Handle topic button click: single-question → immediate execution, multi-question → dropdown
  const handleTopicButtonClick = (label, query, questions, questionCount) => {
    if (questionCount === 1 || !questions || questions.length <= 1) {
      // Single-question: execute immediately with error tracking
      handleTopicClick(query, label);
      setExpandedTopic(null);
    } else {
      // Multi-question: toggle dropdown panel listing all questions
      if (expandedTopic && expandedTopic.label === label) {
        setExpandedTopic(null);
      } else {
        setExpandedTopic({ label, questions });
      }
    }
  };

  // Determine effective quick-access buttons (pinned overrides take precedence)
  const effectiveLabel1 = pinnedBtn1Label || allAnalytics[0]?.label || 'Analytics';
  const effectiveQuery1 = pinnedBtn1Query || allAnalytics[0]?.query || '';
  const effectiveLabel2 = pinnedBtn2Label || (allAnalytics[1]?.label !== effectiveLabel1 ? allAnalytics[1]?.label : allAnalytics[2]?.label) || '';
  const effectiveQuery2 = pinnedBtn2Query || (allAnalytics[1]?.label !== effectiveLabel1 ? allAnalytics[1]?.query : allAnalytics[2]?.query) || '';
  const usedLabels = new Set([effectiveLabel1.toLowerCase(), effectiveLabel2.toLowerCase()]);
  const fallback3 = allAnalytics.find(op => !usedLabels.has(op.label.toLowerCase()));
  const effectiveLabel3 = pinnedBtn3Label || fallback3?.label || '';
  const effectiveQuery3 = pinnedBtn3Query || fallback3?.query || '';

  // Items for the dropdown menu (exclude quick-access buttons)
  const btnLabels = new Set(
    [effectiveLabel1, effectiveLabel2, effectiveLabel3]
      .map(l => l.toLowerCase().trim())
      .filter(Boolean)
  );
  const menuItems = allAnalytics.filter(op => !btnLabels.has(op.label.toLowerCase().trim()));

  // Pin a menu item as a quick-access button (shifts existing buttons down)
  // TODO: Add per-item remove/reset support for pinned buttons in a future iteration
  const pinButton = (op) => {
    // Shift button 2 → button 3
    setPinnedBtn3Label(pinnedBtn2Label || effectiveLabel2);
    setPinnedBtn3Query(pinnedBtn2Query || effectiveQuery2);
    localStorage.setItem(`pnp_analytics_lbl3_${clientId}`, pinnedBtn2Label || effectiveLabel2);
    localStorage.setItem(`pnp_analytics_q3_${clientId}`, pinnedBtn2Query || effectiveQuery2);
    // Shift button 1 → button 2
    setPinnedBtn2Label(effectiveLabel1);
    setPinnedBtn2Query(effectiveQuery1);
    localStorage.setItem(`pnp_analytics_lbl2_${clientId}`, effectiveLabel1);
    localStorage.setItem(`pnp_analytics_q2_${clientId}`, effectiveQuery1);
    // Set new button 1
    setPinnedBtn1Label(op.label);
    setPinnedBtn1Query(op.query);
    localStorage.setItem(`pnp_analytics_lbl_${clientId}`, op.label);
    localStorage.setItem(`pnp_analytics_q_${clientId}`, op.query);
    setShowAnalyticsMenu(false);
  };

  // Blue palette for pill buttons
  const pillColors = ['#1565C0', '#1976D2', '#1E88E5'];

  // Loading state — show a skeleton/spinner
  if (loading && topics.length === 0) {
    return (
      <fieldset style={{ display: 'inline-flex', alignItems: 'center', gap: '0', margin: 0, padding: '0 4px 0 0', border: '1.5px solid #90CAF9', borderRadius: '20px', background: '#E3F2FD', position: 'relative' }}>
        <legend style={{ fontSize: '10px', fontWeight: 700, color: '#1565C0', padding: '0 6px', marginLeft: '8px' }}>📊 Analytics</legend>
        <span style={{ padding: '4px 12px', fontSize: '11px', color: '#666' }}>⏳ Loading topics...</span>
      </fieldset>
    );
  }

  // Error state — show error message with retry option
  if (error && topics.length === 0) {
    return (
      <fieldset style={{ display: 'inline-flex', alignItems: 'center', gap: '0', margin: 0, padding: '0 4px 0 0', border: '1.5px solid #FFCDD2', borderRadius: '20px', background: '#FFEBEE', position: 'relative' }}>
        <legend style={{ fontSize: '10px', fontWeight: 700, color: '#C62828', padding: '0 6px', marginLeft: '8px' }}>📊 Analytics</legend>
        <span style={{ padding: '4px 12px', fontSize: '11px', color: '#C62828' }}>⚠️ {error}</span>
      </fieldset>
    );
  }

  return (
    <fieldset style={{ display: 'inline-flex', alignItems: 'center', gap: '0', margin: 0, padding: '0 4px 0 0', border: '1.5px solid #90CAF9', borderRadius: '20px', background: '#E3F2FD', position: 'relative' }}>
      <legend style={{ fontSize: '10px', fontWeight: 700, color: '#1565C0', padding: '0 6px', marginLeft: '8px' }}>
        📊 Analytics
        <button
          onClick={(e) => { e.stopPropagation(); setShowAnalyticsConfig(!showAnalyticsConfig); setShowAnalyticsMenu(false); }}
          title="Configure visible button count (2 or 3)"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', padding: '0 2px', color: '#1565C0', verticalAlign: 'middle' }}
        >⚙️</button>
      </legend>

      {/* Configuration popover — choose 2 or 3 visible quick buttons */}
      {showAnalyticsConfig && (
        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 110, background: '#fff', border: '1px solid #90CAF9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '8px 12px', marginTop: '4px', minWidth: '160px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>Quick Buttons</div>
          {[2, 3].map(n => (
            <label key={n} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '3px 0', fontSize: '11px', color: '#555' }}>
              <input
                type="radio"
                name="analyticsBtnCount"
                checked={analyticsBtnCount === n}
                onChange={() => {
                  setAnalyticsBtnCount(n);
                  localStorage.setItem(`pnp_analytics_btncount_${clientId}`, n);
                  setShowAnalyticsConfig(false);
                }}
                style={{ margin: 0 }}
              />
              {n} buttons
            </label>
          ))}
        </div>
      )}

      {/* Dropdown menu toggle (▼ Menu) */}
      <button
        onClick={() => { setShowAnalyticsMenu(!showAnalyticsMenu); setShowAnalyticsConfig(false); }}
        style={{ padding: '4px 8px', border: 'none', fontSize: '11px', background: 'transparent', cursor: 'pointer', color: '#333' }}
      >{showAnalyticsMenu ? '▲' : '▼'} Menu</button>

      {/* Dropdown menu with remaining topics */}
      {showAnalyticsMenu && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #90CAF9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '240px', marginTop: '4px' }}>
          {menuItems.map((op, i) => (
            <div key={op.id || i} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
              <button
                onClick={() => { onQuery(op.query); setShowAnalyticsMenu(false); }}
                style={{ flex: 1, padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', textAlign: 'left', color: '#333' }}
              >{op.icon || '📊'} {op.label}</button>
              <button
                onClick={() => pinButton(op)}
                title={`Set "${op.label}" as your quick button`}
                style={{ padding: '8px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', color: '#1565C0', fontWeight: 700 }}
              >⊕</button>
            </div>
          ))}
          {/* Custom query input */}
          <div style={{ borderTop: '1px solid #90CAF9', padding: '6px 12px' }}>
            <input
              placeholder="✏️ Add your own analytics query..."
              onKeyDown={e => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  const label = e.target.value.trim().substring(0, 40);
                  // Check for duplicates (case-insensitive)
                  const allLabels = new Set([
                    ...allAnalytics.map(op => op.label.toLowerCase().trim()),
                  ]);
                  if (!allLabels.has(label.toLowerCase())) {
                    const newItem = { id: `custom_${Date.now()}`, label, query: e.target.value.trim(), icon: '📊' };
                    const updated = [...customAnalyticsItems, newItem];
                    setCustomAnalyticsItems(updated);
                    localStorage.setItem(`pnp_analytics_custom_${clientId}`, JSON.stringify(updated));
                  }
                  onQuery(e.target.value.trim());
                  setShowAnalyticsMenu(false);
                  e.target.value = '';
                }
              }}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #90CAF9', borderRadius: '6px', fontSize: '11px', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      )}

      {/* Quick-access pill buttons */}
      {effectiveLabel1 && (() => {
        const t1 = allAnalytics.find(op => op.label === effectiveLabel1);
        const isFailed1 = failedTopics[effectiveLabel1];
        return (
          <button
            onClick={() => handleTopicButtonClick(effectiveLabel1, effectiveQuery1, t1?.questions, t1?.question_count)}
            style={{ padding: '3px 10px', background: isFailed1 ? 'transparent' : pillColors[0], color: isFailed1 ? '#E65100' : '#fff', border: isFailed1 ? '1.5px solid #E65100' : 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
            title={isFailed1 ? `⚠️ Temporarily unavailable: ${isFailed1}` : undefined}
          >{isFailed1 ? '⚠️ ' : ''}{effectiveLabel1}</button>
        );
      })()}
      {effectiveLabel2 && (() => {
        const t2 = allAnalytics.find(op => op.label === effectiveLabel2);
        const isFailed2 = failedTopics[effectiveLabel2];
        return (
          <button
            onClick={() => handleTopicButtonClick(effectiveLabel2, effectiveQuery2, t2?.questions, t2?.question_count)}
            style={{ padding: '3px 10px', background: isFailed2 ? 'transparent' : pillColors[1], color: isFailed2 ? '#E65100' : '#fff', border: isFailed2 ? '1.5px solid #E65100' : 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
            title={isFailed2 ? `⚠️ Temporarily unavailable: ${isFailed2}` : undefined}
          >{isFailed2 ? '⚠️ ' : ''}{effectiveLabel2}</button>
        );
      })()}
      {analyticsBtnCount >= 3 && effectiveLabel3 && (() => {
        const t3 = allAnalytics.find(op => op.label === effectiveLabel3);
        const isFailed3 = failedTopics[effectiveLabel3];
        return (
          <button
            onClick={() => handleTopicButtonClick(effectiveLabel3, effectiveQuery3, t3?.questions, t3?.question_count)}
            style={{ padding: '3px 10px', background: isFailed3 ? 'transparent' : pillColors[2], color: isFailed3 ? '#E65100' : '#fff', border: isFailed3 ? '1.5px solid #E65100' : 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
            title={isFailed3 ? `⚠️ Temporarily unavailable: ${isFailed3}` : undefined}
          >{isFailed3 ? '⚠️ ' : ''}{effectiveLabel3}</button>
        );
      })()}

      {/* Expanded multi-question dropdown panel */}
      {expandedTopic && (
        <div ref={expandedDropdownRef} style={{ position: 'absolute', top: '100%', left: 0, zIndex: 105, background: '#fff', border: '1px solid #90CAF9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '280px', marginTop: '4px' }}>
          <div style={{ padding: '6px 12px', borderBottom: '1px solid #E3F2FD', fontSize: '11px', fontWeight: 700, color: '#1565C0' }}>
            📊 {expandedTopic.label} — Select a question
          </div>
          {expandedTopic.questions.map((q, i) => (
            <button key={i}
              onClick={() => { handleTopicClick(q.question || q, expandedTopic.label); setExpandedTopic(null); }}
              style={{ display: 'block', width: '100%', padding: '8px 12px', border: 'none', borderBottom: '1px solid #f0f0f0', background: 'transparent', cursor: 'pointer', fontSize: '12px', textAlign: 'left', color: '#333' }}
            >{q.question || q}</button>
          ))}
        </div>
      )}
    </fieldset>
  );
}

export default AnalyticsFieldset;
